import { useEffect, useId, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react'

import { desktopViewAt, type RecordedToken } from './desktopMerge'

import styles from './RecordedActivity.module.scss'

import type { LiveReplayResponse } from '@/api/internal'

type Timing = NonNullable<LiveReplayResponse['timing']>
type Socket = Timing['sockets'][number]
type Token = Socket['batches'][number]['tokens'][number]

/** Rows of the desktop view kept on screen; desktop scrolls to its newest rows the same way. */
const DESKTOP_VIEW_ROWS = 8

const providerName = (provider: Socket['provider']) => (provider === 'muse' ? 'Muse' : 'Soniox')

const socketHasText = (socket: Socket): boolean =>
  socket.batches.every((batch) => batch.tokens.every((token) => typeof token.text === 'string'))

/** Whether every recorded connection kept its token text; captures made before text was recorded have none. */
export const hasTokenText = (timing: LiveReplayResponse['timing']): boolean =>
  timing?.status !== undefined &&
  timing.status !== 'missing' &&
  timing.sockets.length > 0 &&
  timing.sockets.every(socketHasText)

const recordedToken = (token: Token): RecordedToken => ({
  text: token.text ?? '',
  isFinal: token.is_final,
  speakerId: token.speaker_id,
  startMs: token.start_ms,
  endMs: token.end_ms,
  isEndpoint: token.is_endpoint ?? false,
})

const warningMessages: Record<string, string> = {
  invalid_capture: 'Some recordings could not be read because their data is invalid.',
  read_limit: 'This view reached its reading limit. Some recorded activity is not shown.',
  capture_truncated: 'Some batches or tokens were not captured. Gaps may be missing from this view.',
  read_failed: 'Some recordings could not be loaded. The estimated transcript is still available.',
}

const formatTime = (ms: number): string => `${(ms / 1000).toFixed(3)} s`

const arrivalLabel = (atMs: number | null): string => {
  if (atMs === null) return 'Arrival time unavailable'
  return `${formatTime(atMs)} since acknowledgement`
}

export const RecordedActivity = ({ timing }: { timing: LiveReplayResponse['timing'] }) => {
  if (!timing || timing.status === 'missing') {
    return (
      <section className={styles.panel} aria-label="Server-observed activity">
        <h3 className={styles.title}>Server-observed activity</h3>
        <p className={styles.description}>
          No timing capture is available. The transcript uses estimated arrival times.
        </p>
      </section>
    )
  }
  return <CapturedActivity timing={timing} />
}

const CapturedActivity = ({ timing }: { timing: Timing }) => {
  const [selectedId, setSelectedId] = useState(timing.sockets[0]?.socket_id ?? '')
  const socket = timing.sockets.find((item) => item.socket_id === selectedId) ?? timing.sockets[0]
  const selectorId = useId()
  const warnings = Array.from(
    new Set(timing.warnings.map((code) => warningMessages[code] ?? 'Some recorded activity is unavailable.'))
  )

  return (
    <section className={styles.panel} aria-label="Server-observed activity">
      <h3 className={styles.title}>Server-observed activity</h3>
      <p className={styles.description}>
        Recorded provider batches, on each connection&apos;s own clock. These controls are independent of the estimated
        transcript. Desktop delivery times were not recorded.
      </p>
      {timing.status === 'partial' ? <p className={styles.notice}>Timing capture is incomplete.</p> : null}
      {warnings.map((message) => (
        <p key={message} className={styles.notice}>
          {message}
        </p>
      ))}
      {socket ? (
        <>
          <label className={styles.connection} htmlFor={selectorId}>
            Connection
            <select id={selectorId} value={socket.socket_id} onChange={(event) => setSelectedId(event.target.value)}>
              {timing.sockets.map((item, index) => (
                <option key={item.socket_id} value={item.socket_id}>
                  {item.source === 'mic' ? 'Mic' : 'System'} · {providerName(item.provider)} · connection {index + 1}
                </option>
              ))}
            </select>
          </label>
          <SocketActivity key={socket.socket_id} socket={socket} />
        </>
      ) : (
        <p className={styles.description}>
          Timing capture could not be loaded. The estimated transcript is still available.
        </p>
      )}
    </section>
  )
}

const SocketActivity = ({ socket }: { socket: Socket }) => {
  const [index, setIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const batch = socket.batches[index]
  const nextBatch = socket.batches[index + 1]
  const canPlay = batch?.at_ms != null && nextBatch?.at_ms != null && nextBatch.at_ms >= batch.at_ms
  const hasBatches = socket.batches.length > 0
  const tokenBatches = useMemo(
    () => (socketHasText(socket) ? socket.batches.map((item) => item.tokens.map(recordedToken)) : null),
    [socket]
  )

  useEffect(() => {
    if (!isPlaying || !canPlay || batch.at_ms === null || nextBatch.at_ms === null) return
    const timer = window.setTimeout(() => {
      const following = socket.batches[index + 2]
      setIndex(index + 1)
      if (following?.at_ms == null || nextBatch.at_ms === null || following.at_ms < nextBatch.at_ms) {
        setIsPlaying(false)
      }
    }, nextBatch.at_ms - batch.at_ms)
    return () => window.clearTimeout(timer)
  }, [isPlaying, canPlay, batch, nextBatch, index, socket.batches])

  const seek = (nextIndex: number) => {
    setIsPlaying(false)
    setIndex(nextIndex)
  }

  return (
    <>
      <div className={styles.controls}>
        <button
          type="button"
          className={styles.button}
          aria-label={isPlaying ? 'Pause recorded activity' : 'Play recorded activity'}
          onClick={() => setIsPlaying((playing) => !playing)}
          disabled={!canPlay}
        >
          {isPlaying ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <button
          type="button"
          className={styles.button}
          aria-label="Previous recorded batch"
          onClick={() => seek(index - 1)}
          disabled={!hasBatches || index === 0}
        >
          <ChevronLeft size={16} />
        </button>
        <input
          type="range"
          className={styles.scrubber}
          min={0}
          max={Math.max(0, socket.batches.length - 1)}
          value={index}
          disabled={socket.batches.length < 2}
          onChange={(event) => seek(Number(event.target.value))}
          aria-label="Recorded batch"
          aria-valuetext={
            batch ? `Batch ${index + 1} of ${socket.batches.length}, ${arrivalLabel(batch.at_ms)}` : 'No batches'
          }
        />
        <button
          type="button"
          className={styles.button}
          aria-label="Next recorded batch"
          onClick={() => seek(index + 1)}
          disabled={!hasBatches || index === socket.batches.length - 1}
        >
          <ChevronRight size={16} />
        </button>
      </div>
      {batch ? (
        <>
          <p className={styles.position}>
            Batch {index + 1} of {socket.batches.length} · {arrivalLabel(batch.at_ms)}
          </p>
          {batch.at_ms === null || (nextBatch && !canPlay) ? (
            <p className={styles.description}>
              Timing is unavailable across this step. Use the arrow buttons to continue.
            </p>
          ) : null}
          {tokenBatches ? (
            <DesktopView batches={tokenBatches} index={index} truncated={socket.truncated_tokens > 0} />
          ) : (
            <p className={styles.description}>
              This capture predates token text, so it shows timing and speakers without words.
            </p>
          )}
          <BatchTokens batch={batch} />
        </>
      ) : (
        <p className={styles.description}>This connection has no recorded batches.</p>
      )}
      {socket.dropped_batches > 0 || socket.truncated_tokens > 0 ? (
        <p className={styles.notice}>
          Capture omitted {socket.dropped_batches} batches and {socket.truncated_tokens} tokens.
        </p>
      ) : null}
      <p className={styles.description}>
        Speaker IDs apply only to this connection. Audio positions are measured from its audio start; receipt times are
        measured from its acknowledgement. Each step lists only that batch&apos;s tokens; the desktop view merges every
        batch up to it on this connection alone, so mic and system rows are not interleaved. Rows of a translated
        meeting can differ, since desktop splits those by translation.
      </p>
    </>
  )
}

const BatchTokens = ({ batch }: { batch: Socket['batches'][number] }) => {
  if (batch.tokens.length === 0) return <p className={styles.description}>No timed tokens in this batch.</p>
  return (
    <ol className={styles.tokens} aria-label="Tokens in recorded batch">
      {batch.tokens.map((token, index) => (
        <li key={index} className={styles.token}>
          <div className={styles.tokenHeader}>
            <span>{token.speaker_id === null ? 'Speaker unknown' : `Speaker ${token.speaker_id}`}</span>
            <span className={token.is_final ? styles.final : styles.partial}>
              {token.is_final ? 'Final' : 'Partial'}
            </span>
            {token.replayed ? <span className={styles.replayed}>Replayed audio</span> : null}
            {token.is_endpoint ? <span className={styles.replayed}>Endpoint</span> : null}
          </div>
          {typeof token.text === 'string' ? (
            <p className={styles.tokenText} aria-label="Token text">
              {JSON.stringify(token.text)}
            </p>
          ) : null}
          <p className={styles.tokenDetail}>
            Audio position: {formatTime(token.start_ms)} to {formatTime(token.end_ms)}
          </p>
          <p className={styles.tokenDetail}>
            Audio received:{' '}
            {token.audio_arrival_ms === null
              ? 'unavailable'
              : `${formatTime(token.audio_arrival_ms)} since acknowledgement`}
          </p>
        </li>
      ))}
    </ol>
  )
}

const DesktopView = ({
  batches,
  index,
  truncated,
}: {
  batches: readonly (readonly RecordedToken[])[]
  index: number
  truncated: boolean
}) => {
  const rows = useMemo(() => desktopViewAt(batches, index), [batches, index])
  const shown = rows.slice(-DESKTOP_VIEW_ROWS)
  return (
    <div className={styles.desktopView}>
      <p className={styles.description}>
        Desktop view after this batch
        {rows.length > shown.length ? ` · last ${shown.length} of ${rows.length} rows` : ''}
      </p>
      {truncated ? (
        <p className={styles.notice}>
          Some tokens were not captured, so these rows can differ from what desktop showed.
        </p>
      ) : null}
      {shown.length === 0 ? (
        <p className={styles.description}>Desktop shows no rows yet.</p>
      ) : (
        <ol className={styles.desktopRows} aria-label="Desktop rows after this batch">
          {shown.map((row, rowIndex) => (
            <li key={rows.length - shown.length + rowIndex} className={styles.desktopRow}>
              <span className={styles.desktopSpeaker}>Speaker {row.speakerId}</span>
              <p className={styles.desktopText}>
                {row.text}
                {row.interim ? <span className={styles.interim}>{row.interim}</span> : null}
              </p>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
